import { z } from 'zod';

export const ShowInsightsInputSchema = z.object({
  insights: z
    .object({
      text: z
        .string()
        .describe(
          'A single-sentence, one-line factual insight derived from prior tool results, in human-friendly language. Must carry its numbers — the magnitude with its unit (rate, volume, count) and the relative size (percent change, or percent deviation from normal/expected) — with the key figures emphasized in Markdown (*like this*); an insight without emphasis is malformed. Dates read as plain language ("mid-May", "around end of March", "June 11") — never raw ISO 8601 or timestamp strings. Example: "Production rate *increased ~12%* from 26 to 29 MSTB/d around *end of March*"',
        ),
      marker: z
        .string()
        .optional()
        .describe(
          'Byte-exact label of an x-axis chart marker (reference line or area) the preceding dashboard plotted, as listed in the handoff message — cite it whenever the insight describes the dated event or period that marker annotates. This field is the only place the label appears: never quote or restate it inside the insight text. When no plotted marker corresponds, omit the field entirely — never fill it with a placeholder like "-", "none", or "N/A"',
        ),
    })
    .array()
    .min(1)
    .max(5)
    .describe(
      'Up to 5 one-line key insights derived from statistical analysis and other results from prior tool calls, ordered by descending deviation — the finding furthest from normal/expected first — then by recency, most recent first',
    ),
  followUpQuestions: z
    .string()
    .array()
    .min(0)
    .max(5)
    .describe(
      'A list of relevant follow-up questions the user can ask to dive deeper into the results, stated concisely in human-friendly language. Example: "Show me the sales breakdown by region" or "What were the top-performing products?"',
    ),
});

export const ShowInsightsOutputSchema = z.object({
  success: z.literal(true),
});

export type ShowInsightsInput = z.infer<typeof ShowInsightsInputSchema>;
export type ShowInsightsOutput = z.infer<typeof ShowInsightsOutputSchema>;
